import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from '@mui/material/styles';
import { styled } from "@mui/material/styles";
import React from "react";

const ProcessPathRoot = styled("section")({
});

const Intro = styled(Box)(() => ({
  maxWidth: 760,
  margin: "0 auto clamp(3rem, 6vw, 5rem)",
  textAlign: "center",
}));

const Stage = styled(Box)(({ theme }) => ({
  position: "relative",
  maxWidth: 1200,
  minHeight: 300,
  margin: "0 auto",
  overflow: "visible",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  '& h3':{
    margin: "1em 0 0.5em 0",
    [theme.breakpoints.down("md")]: {
      marginTop: "0.25em",
    },
    fontSize: "1.4rem !important",
    lineHeight: 1.2,
  },
  '& .text': {
    lineHeight: 1.6,
    fontSize: "1rem !important",
  }
}));

const Line = styled("svg")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: 320,
  color: (theme.vars || theme).palette.divider,
  overflow: "visible",
  pointerEvents: "none",
  '& .badge-circle': {
    fill: (theme.vars || theme).palette.background.paper,
    r: 42,
    strokeWidth: 7,
  },
  '& .badge-text': {
    dominantBaseline: "central",
    textAnchor: "middle",
    fontSize: "36px",
    fontWeight: "700",
    fill: 'currentColor',
    color: (theme.vars || theme).palette.text.primary,
    background: (theme.vars || theme).palette.background.paper,
  },

  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const Steps = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 1,
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "clamp(1rem, 3vw, 2rem)",
  alignItems: "start",

  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gap: "1.25rem",
  },
}));

const Step = styled("article")(({ theme }) => ({
  position: "relative",

  "&:nth-of-type(1)": {
    marginTop: 140,
  },

  "&:nth-of-type(2)": {
    marginTop: 0,
  },

  "&:nth-of-type(3)": {
    marginTop: 180,
  },

  "&:nth-of-type(4)": {
    marginTop: -30,
  },

  [theme.breakpoints.down("md")]: {
    maxWidth: "none",
    marginTop: "0 !important",
    padding: "1.25rem",
    border: `1px solid ${(theme.vars || theme).palette.divider}`,
    borderRadius: "1.25rem",
    background: (theme.vars || theme).palette.background.paper,
    color: (theme.vars || theme).palette.text.primary,
  },
}));

const steps = [
  {
    number: "1",
    title: "Verstehen",
    text: "Wir gehen strukturiert vor – damit Projekte nicht im Chaos enden, sondern sauber umgesetzt werden.",
  },
  {
    number: "2",
    title: "Planen",
    text: "Klare Struktur, sinnvolle Technologien und realistische Umsetzung",
  },
  {
    number: "3",
    title: "Umsetzen & testen",
    text: "Entwicklung in einer sicheren Umgebung mit transparentem Fortschritt",
  },
  {
    number: "4",
    title: "Betreiben & weiterentwickeln",
    text: "Betreuung, Optimierung und langfristige Stabilität",
  },
];

export default function ProcessPath() {
  const theme = useTheme();

  return (
    <ProcessPathRoot aria-labelledby="process-title">
      <Intro>

        <Typography
          id="process-title"
          component="h2"
          sx={{
            lineHeight: 1.1,
          }}
        >
          So arbeiten wir
        </Typography>

        <Typography
          sx={{
            lineHeight: 1.7,
          }}
        >
          Wir gehen <span className="underlined">strukturiert</span> vor – damit Projekte 
          nicht im Chaos enden, sondern <span className="underlined">sauber</span> umgesetzt werden.
        </Typography>
      </Intro>

      <Stage>
        <Line
          viewBox="0 0 1400 320"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="lineGradient">
              <stop offset="0%" stopColor={(theme.vars || theme).palette.divider} stopOpacity="0" />
              <stop offset="20%" stopColor={(theme.vars || theme).palette.divider} stopOpacity="1" />
              <stop offset="50%" stopColor={(theme.vars || theme).palette.brand.main} stopOpacity="1" />
              <stop offset="80%" stopColor={(theme.vars || theme).palette.highlight.main} stopOpacity="1" />
              <stop offset="100%" stopColor={(theme.vars || theme).palette.error.main} stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Linie */}
          <path
            d="
              M -120 210
              C 80 40, 230 70, 340 170
              S 520 310, 640 180
              S 820 20, 930 120
              S 1120 260, 1380 150
            "
            fill="none"
            stroke="url(#lineGradient)"
            strokeWidth="7"
            strokeLinecap="round"
          />

          {/* Badge 1 */}
          <g transform="translate(160 95)">
            <circle
              className="badge-circle"
              stroke={(theme.vars || theme).palette.divider}
            />
            <text className="badge-text">
              1
            </text>
          </g>

          {/* Badge 2 */}
          <g transform="translate(520 255)">
            <circle
              className="badge-circle"
              stroke={(theme.vars || theme).palette.brand.main}
            />
            <text className="badge-text">
              2
            </text>
          </g>

          {/* Badge 3 */}
          <g transform="translate(880 80)">
            <circle
              className="badge-circle"
              stroke={(theme.vars || theme).palette.highlight.light}
            />
            <text className="badge-text">
              3
            </text>
          </g>

          {/* Badge 4 */}
          <g transform="translate(1240 200)">
            <circle
              className="badge-circle"
              stroke={(theme.vars || theme).palette.error.main}
            />
            <text className="badge-text">
              4
            </text>
          </g>
        </Line>

        <Steps>
          {steps.map((step) => (
            <Step key={step.number}>
              <h3>
                {step.title}
              </h3>
              <span className="text">
                {step.text}
              </span>
            </Step>
          ))}
        </Steps>
      </Stage>
    </ProcessPathRoot>
  );
}
